import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Scale, FileText, Lock } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function LegalPage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Law Firms',
    description: 'Protecting client confidentiality and privileged information',
    url: 'https://thecisonetwork.com/sectors/legal',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Legal',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Law Firms"
        subtitle="Protecting client confidentiality and privileged information in an era of sophisticated cyber threats"
        eyebrow="Legal Sector"
        backgroundImage={getSectorCard('legal')?.image}
        backgroundAlt={getSectorCard('legal')?.imageAlt}
        seoTitle="Fractional CISO Law Firms | The CISO Network"
        seoDescription="Expert fractional CISO services for law firms. Protect client confidentiality, meet SRA obligations, and prevent costly data breaches that damage reputation."
      />

      {/* Why Law Firms Need CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Law Firms
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Law firms are treasure troves of valuable information. M&A transactions,
                  intellectual property, commercial disputes, and personal client matters all
                  generate sensitive data that attackers covet. The legal sector has become a prime
                  target - 28% of UK law firms reported cyber attacks in 2023, and the true figure
                  is likely far higher given underreporting.
                </p>
                <p>
                  The stakes couldn't be higher. A single breach can expose privileged client
                  communications, derail billion-pound transactions, and destroy reputations built
                  over decades. The SRA's 2023 report found that 75% of law firms hold client money,
                  making them attractive targets for financial fraud as well as data theft.
                </p>
                <p>
                  Regulatory pressure is intensifying. The Solicitors Regulation Authority (SRA) now
                  requires firms to demonstrate{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    cybersecurity competence
                  </Link>{' '}
                  as part of its Standards and Regulations. Cyber insurance premiums are rising, and
                  underwriters increasingly demand evidence of security leadership before providing
                  coverage. Yet many firms, particularly mid-market practices, lack the internal
                  expertise to meet these expectations.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Scale className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      SRA Compliance
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Navigate SRA requirements with confidence. Our CISOs understand the Code of
                      Conduct, Accounts Rules, and the specific cybersecurity expectations placed on
                      legal practices.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <FileText className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Client Confidentiality
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Protect the attorney-client privilege that underpins your practice. Our CISOs
                      implement controls to safeguard privileged communications and work product.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              When Law Firms Are Compromised
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Real incidents where legal practices suffered breaches that a CISO could have
              prevented
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Mossack Fonseca (2016)',
                impact: '2.6TB data leaked, firm closed down',
                description:
                  'The Panama Papers breach exposed how the firm helped clients evade tax. A CISO would have implemented proper access controls, data classification, and external perimeter security - basic measures that were missing and allowed the breach to occur.',
              },
              {
                icon: AlertTriangle,
                title: 'Grubman Shire Meiselas (2020)',
                impact: '756GB data stolen, $21M ransom demanded',
                description:
                  'The entertainment law firm representing stars like Madonna and Drake was hit by REvil ransomware. A CISO would have ensured robust backup systems, endpoint protection, and incident response capabilities to prevent or quickly recover from such attacks.',
              },
              {
                icon: AlertTriangle,
                title: 'SRA Cyber Attack Survey (2023)',
                impact: '£4M+ stolen from client accounts',
                description:
                  'The SRA found that email compromise attacks resulted in millions being diverted from client accounts. A CISO would have implemented email authentication (DMARC), verification procedures for payment changes, and staff training to recognise phishing.',
              },
              {
                icon: AlertTriangle,
                title: 'Tuckers Solicitors (2022)',
                impact: '£98,000 ICO fine (first ransomware fine), criminal data exposed',
                description:
                  'The criminal law firm suffered a ransomware attack exposing sensitive client data. The ICO issued its first fine specifically for a ransomware attack, finding their security practices inadequate. A CISO would have established encryption, access controls, and security patching procedures.',
              },
              {
                icon: AlertTriangle,
                title: "Campbell Conroy & O'Neil (2021)",
                impact: 'Data of 170,000+ individuals exposed',
                description:
                  'The US law firm suffered a breach exposing sensitive data including Social Security numbers and medical information. A CISO would have implemented network monitoring and threat detection to identify and respond to the attack earlier.',
              },
              {
                icon: AlertTriangle,
                title: 'Law Firm Phishing Epidemic',
                impact: '£4M+ in client funds stolen annually',
                description:
                  'The SRA reports that email modification fraud targeting law firms is rampant. A CISO would establish verification protocols, train staff to identify suspicious communications, and implement technical controls to detect account compromise.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks Facing Law Firms
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats that put client confidentiality and firm reputation at risk
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Email Compromise',
                description:
                  'Sophisticated attacks intercept and modify payment instructions, diverting client funds to criminal accounts.',
              },
              {
                icon: FileText,
                title: 'Ransomware',
                description:
                  'Attackers encrypt case files and threaten to publish privileged information unless ransoms are paid.',
              },
              {
                icon: Lock,
                title: 'Supply Chain Attacks',
                description:
                  "Barristers' chambers, expert witnesses, and conveyancing partners create pathways into your systems.",
              },
              {
                icon: Scale,
                title: 'Reputational Damage',
                description:
                  'A single breach can destroy client trust and professional reputation built over decades of practice.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Legal Services - FAQs"
        subtitle="Common questions from law firms and legal service providers"
        items={[
          {
            id: 'legal-1',
            question: 'How does a fractional CISO work with law firms?',
            answer:
              "Our CISOs work with law firms of all sizes, from boutique practices to large national firms. They understand the SRA's expectations for cybersecurity governance and provide practical guidance that protects client confidential information without disrupting legal practice.",
          },
          {
            id: 'legal-2',
            question: 'What cybersecurity obligations do law firms have?',
            answer:
              'Law firms must protect client confidential information under the SRA Code of Conduct, comply with GDPR for client data, and increasingly meet the security requirements of corporate clients and cyber insurers. The SRA now expects firms to demonstrate active cybersecurity governance.',
          },
          {
            id: 'legal-3',
            question: 'How does a fractional CISO help with SRA compliance?',
            answer:
              'Our CISOs help law firms implement the cybersecurity measures the SRA expects: incident response plans, data protection controls, staff training, and vendor risk management. We prepare firms for SRA scrutiny and help them demonstrate that cybersecurity is treated with the same seriousness as client confidentiality.',
          },
          {
            id: 'legal-4',
            question: 'What is the biggest cyber risk for law firms?',
            answer:
              'Ransomware and business email compromise are the most common and damaging attacks against law firms. The Maze and Clop ransomware groups have specifically targeted legal practices. A fractional CISO implements the controls that prevent these attacks and ensures rapid recovery if they occur.',
          },
          {
            id: 'legal-5',
            question: 'Can a fractional CISO help with client security requirements?',
            answer:
              'Yes. Corporate clients increasingly require law firms to complete security questionnaires and demonstrate compliance with standards like ISO 27001. Our CISOs help firms build the security documentation and controls needed to win and retain major client relationships.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Protect Your Clients and Your Practice
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Client confidentiality is the foundation of legal practice. Our fractional CISOs
            understand the unique risks facing law firms and how to defend against them.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
